import React from 'react';
import { convertWarekiDetail } from '@/utils/wareki';
import { getServiceTypeLabel } from '@/constants/serviceType';
import type { AdminInformationItem } from '@/services/admin/informations';

interface AdminInformationDetailProps {
  data: AdminInformationItem;
  showTenantField?: boolean;
}

/**
 * Format "YYYY-MM-DD" to 和暦 "令和XX年MM月DD日".
 */
const formatWarekiDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const era = convertWarekiDetail(y, m, d);
  if (!era) return '';
  return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
};

/**
 * Format "YYYY-MM-DD HH:mm" to 和暦 date + time.
 */
const formatWarekiDateTime = (dtStr: string | null): string => {
  if (!dtStr) return '';
  const [datePart, timePart] = dtStr.split(' ');
  const dateFormatted = formatWarekiDate(datePart);
  if (!dateFormatted) return '';
  return timePart ? `${dateFormatted} ${timePart}` : dateFormatted;
};

const AdminInformationDetail: React.FC<AdminInformationDetailProps> = ({
  data,
  showTenantField = true,
}) => {
  const displayPeriod = (() => {
    const from = formatWarekiDateTime(data.display_from);
    const to = formatWarekiDateTime(data.display_to);
    if (!from && !to) return '';
    return `${from}-${to}`;
  })();

  const fields: { label: string; value: string; preserveWhitespace?: boolean }[] = [
    ...(showTenantField
      ? [{ label: '表示対象', value: (data.tenant_names ?? []).join('、') || '' }]
      : []),
    { label: '表示サービス', value: getServiceTypeLabel(data.service_type) },
    { label: 'お知らせ日', value: formatWarekiDate(data.information_date) },
    { label: 'タイトル', value: data.title },
    { label: 'お知らせ内容', value: data.contents ?? '', preserveWhitespace: true },
    { label: '有効期限', value: displayPeriod },
    { label: '表示設定', value: data.is_display ? '表示する' : '表示しない' },
  ];

  return (
    <dl className="grid grid-cols-12 gap-y-2 text-sm">
      {fields.map((item, index) => (
        <div key={index} className="contents">
          <dt className="col-span-3 font-semibold py-1">{item.label}</dt>
          <dd
            className={`col-span-9 py-1 ${item.preserveWhitespace ? 'whitespace-pre-wrap' : ''}`}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default AdminInformationDetail;
